import ReactMarkdown from 'react-markdown';
import { Navigate } from 'react-router-dom';
import remarkGfm from 'remark-gfm';

import { Layout } from '../components/Layout';
import { usePortalConfig } from '../hooks/usePortalConfig';

const markdownComponents: React.ComponentProps<typeof ReactMarkdown>['components'] = {
  h1: ({ children }) => <h1 className="text-2xl font-bold tracking-tight mt-8 mb-3 first:mt-0">{children}</h1>,
  h2: ({ children }) => <h2 className="text-xl font-semibold mt-6 mb-2 pb-1 border-b border-border">{children}</h2>,
  h3: ({ children }) => <h3 className="text-base font-semibold mt-5 mb-1.5">{children}</h3>,
  p: ({ children }) => <p className="text-sm leading-6 mb-3">{children}</p>,
  strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
  em: ({ children }) => <em className="italic">{children}</em>,
  hr: () => <hr className="my-6 border-border" />,
  ul: ({ children }) => <ul className="list-disc list-inside text-sm mb-3 space-y-1">{children}</ul>,
  ol: ({ children }) => <ol className="list-decimal list-inside text-sm mb-3 space-y-1">{children}</ol>,
  li: ({ children }) => <li className="leading-6">{children}</li>,
  code: ({ children }) => (
    <code className="px-1.5 py-0.5 rounded text-xs font-mono bg-muted text-foreground">{children}</code>
  ),
  table: ({ children }) => (
    <div className="overflow-x-auto my-4">
      <table className="w-full text-sm border-collapse">{children}</table>
    </div>
  ),
  thead: ({ children }) => <thead>{children}</thead>,
  tbody: ({ children }) => <tbody>{children}</tbody>,
  tr: ({ children }) => <tr className="border-b border-border">{children}</tr>,
  th: ({ children }) => (
    <th className="text-left px-3 py-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground bg-muted">
      {children}
    </th>
  ),
  td: ({ children }) => <td className="px-3 py-2 align-top">{children}</td>,
};

export const Information = () => {
  const { pages, infoPageContent } = usePortalConfig();

  const pageConfig = pages?.find((p) => p.id === 'information');

  if (pageConfig && !pageConfig.enabled) {
    return <Navigate to="/" replace />;
  }

  return (
    <Layout>
      <div className="p-4 sm:p-6 max-w-4xl">
        {infoPageContent ? (
          <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
            {infoPageContent}
          </ReactMarkdown>
        ) : (
          <p className="text-muted-foreground text-sm">No information content available.</p>
        )}
      </div>
    </Layout>
  );
};
